import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Navbar from "@/components/Navbar";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar
} from "recharts";
import { TrendingUp, TrendingDown, Activity, ChevronLeft, Minus } from "lucide-react";

import { API } from "../lib/api";

function getColor(s) { return s >= 80 ? "#10B981" : s >= 60 ? "#F59E0B" : s >= 40 ? "#F97316" : "#EF4444"; }
function pillarLabel(k) { return k.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()); }

export default function TrendPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/api/trends/me`, { withCredentials: true })
      .then(({ data }) => setData(data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="min-h-screen bg-gray-50"><Navbar />
      <div className="flex justify-center py-20">
        <div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" />
      </div>
    </div>
  );

  const assessments = data?.assessment_trend || [];
  const pulses = data?.pulse_trend || [];
  const latest = assessments[assessments.length - 1];
  const previous = assessments.length >= 2 ? assessments[assessments.length - 2] : null;
  const scoreDiff = latest && previous ? latest.score - previous.score : 0;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="mb-8 animate-slide-up">
          <button onClick={() => navigate("/dashboard")}
            className="flex items-center gap-1 text-sm font-ibm-plex text-slate-500 hover:text-slate-900 mb-4">
            <ChevronLeft className="w-4 h-4" /> Dashboard
          </button>
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp className="w-5 h-5 text-blue-600" />
            <h1 className="font-outfit text-3xl font-black text-slate-900">Trend Analysis</h1>
          </div>
          <p className="font-ibm-plex text-sm text-slate-500">Track your PIQ progression over time</p>
        </div>

        {/* Stats */}
        {assessments.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 animate-slide-up stagger-1">
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <p className="font-ibm-plex text-xs uppercase tracking-widest text-slate-400 mb-1">Latest PIQ</p>
              <p className="font-outfit text-3xl font-black" style={{ color: getColor(latest?.score || 0) }}>{latest?.score || "—"}</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <p className="font-ibm-plex text-xs uppercase tracking-widest text-slate-400 mb-1">Change</p>
              <div className="flex items-center gap-1">
                {scoreDiff > 0 ? <TrendingUp className="w-5 h-5 text-emerald-500" /> :
                 scoreDiff < 0 ? <TrendingDown className="w-5 h-5 text-red-500" /> :
                 <Minus className="w-5 h-5 text-slate-400" />}
                <span className={`font-outfit text-2xl font-black ${scoreDiff > 0 ? "text-emerald-600" : scoreDiff < 0 ? "text-red-600" : "text-slate-400"}`}>
                  {scoreDiff > 0 ? `+${scoreDiff.toFixed(1)}` : scoreDiff.toFixed(1)}
                </span>
              </div>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <p className="font-ibm-plex text-xs uppercase tracking-widest text-slate-400 mb-1">Assessments</p>
              <p className="font-outfit text-3xl font-black text-slate-900">{assessments.length}</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <p className="font-ibm-plex text-xs uppercase tracking-widest text-slate-400 mb-1">Best Score</p>
              <p className="font-outfit text-3xl font-black text-emerald-600">
                {Math.max(...assessments.map(a => a.score))}
              </p>
            </div>
          </div>
        )}

        {/* PIQ Score Trend */}
        {assessments.length >= 2 && (
          <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6 animate-slide-up stagger-2">
            <h2 className="font-outfit text-base font-bold text-slate-900 mb-4">PIQ Score Over Time</h2>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={assessments} margin={{ top: 5, right: 10, bottom: 5, left: -10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                <XAxis dataKey="date" tick={{ fontFamily: "IBM Plex Sans", fontSize: 11, fill: "#9CA3AF" }} />
                <YAxis domain={[0, 100]} tick={{ fontFamily: "IBM Plex Sans", fontSize: 11, fill: "#9CA3AF" }} />
                <Tooltip contentStyle={{ fontFamily: "IBM Plex Sans", fontSize: 12 }}
                  formatter={(v, n, props) => [`${v}/100`, props.payload.name || "PIQ"]} />
                <ReferenceLine y={80} stroke="#10B981" strokeDasharray="4 4" strokeOpacity={0.4} label={{ value: "Healthy", fontSize: 10, fill: "#10B981" }} />
                <ReferenceLine y={60} stroke="#F59E0B" strokeDasharray="4 4" strokeOpacity={0.4} />
                <Line type="monotone" dataKey="score" stroke="#0F172A" strokeWidth={2.5} dot={{ fill: "#0F172A", r: 5 }} activeDot={{ r: 7, fill: "#3B82F6" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Pillar Trends */}
        {assessments.length >= 2 && latest?.pillars && (
          <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6 animate-slide-up stagger-3">
            <h2 className="font-outfit text-base font-bold text-slate-900 mb-4">Pillar Evolution</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Object.entries(latest.pillars).map(([k, v]) => {
                const prev = previous?.pillars?.[k] || v;
                const diff = v - prev;
                return (
                  <div key={k} className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                    <p className="font-ibm-plex text-[10px] uppercase tracking-widest text-slate-400 mb-1">{pillarLabel(k)}</p>
                    <div className="flex items-end gap-2">
                      <span className="font-outfit text-lg font-black" style={{ color: v >= 3 ? "#10B981" : v >= 2 ? "#F59E0B" : "#EF4444" }}>
                        {typeof v === "number" ? v.toFixed(1) : v}
                      </span>
                      {diff !== 0 && (
                        <span className={`font-ibm-plex text-xs font-medium ${diff > 0 ? "text-emerald-600" : "text-red-600"}`}>
                          {diff > 0 ? "+" : ""}{diff.toFixed(1)}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Pulse Trend */}
        {pulses.length >= 2 && (
          <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6 animate-slide-up stagger-4">
            <div className="flex items-center gap-2 mb-4">
              <Activity className="w-4 h-4 text-blue-600" />
              <h2 className="font-outfit text-base font-bold text-slate-900">Weekly Pulse Trend</h2>
            </div>
            <ResponsiveContainer width="100%" height={180}>
              <LineChart data={pulses}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#9CA3AF" }} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: "#9CA3AF" }} />
                <Tooltip contentStyle={{ fontFamily: "IBM Plex Sans", fontSize: 12 }} formatter={(v) => [`${v}/100`, "Pulse"]} />
                <Line type="monotone" dataKey="score" stroke="#3B82F6" strokeWidth={2} dot={{ fill: "#3B82F6", r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {assessments.length < 2 && (
          <div className="bg-white border border-gray-200 rounded-xl p-16 text-center animate-fade-in">
            <TrendingUp className="w-12 h-12 text-slate-200 mx-auto mb-4" />
            <h3 className="font-outfit text-xl font-bold text-slate-900 mb-2">Need more data</h3>
            <p className="font-ibm-plex text-sm text-slate-500 mb-6">
              Complete at least 2 assessments to see your PIQ trend over time.
            </p>
            <button onClick={() => navigate("/project/new")}
              className="bg-slate-900 text-white font-outfit font-semibold px-6 py-3 rounded-lg hover:bg-slate-800 transition-colors">
              New Assessment
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
